import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { getCurrentUser } from 'app/core/store/currentUser/user.selectors';
import { SnackbarService } from '../../common/services/snackbar.service';
import { FormLayoutService } from '../../form-baselayout/form-layout/form-layout.service';
import {
  ProjectDetailsMode,
  ProjectDetailsTableData,
  ConsultantProjectRateRow,
  ProjectTaskRow,
  ThemeAllocationRow,
  PrepaymentDetailRow
} from '../models/project-details.types';

@Injectable({
  providedIn: 'root'
})
export class ProjectDetailsStateService {

  // Mode
  private modeSubject = new BehaviorSubject<ProjectDetailsMode>('add');
  mode$: Observable<ProjectDetailsMode> = this.modeSubject.asObservable();

  // Selected Client / Project Info
  private selectedClientSubject = new BehaviorSubject<string>('');
  selectedClient$: Observable<string> = this.selectedClientSubject.asObservable();

  private selectedClientCodeSubject = new BehaviorSubject<string>('');
  selectedClientCode$: Observable<string> = this.selectedClientCodeSubject.asObservable();

  private selectedProjectSubject = new BehaviorSubject<string>('');
  selectedProject$: Observable<string> = this.selectedProjectSubject.asObservable();

  private selectedProjectCodeSubject = new BehaviorSubject<string>('');
  selectedProjectCode$: Observable<string> = this.selectedProjectCodeSubject.asObservable();

  private selectedCountryCodeSubject = new BehaviorSubject<string>('');
  selectedCountryCode$: Observable<string> = this.selectedCountryCodeSubject.asObservable();

  private selectedCountryNameSubject = new BehaviorSubject<string>('');
  selectedCountryName$: Observable<string> = this.selectedCountryNameSubject.asObservable();

  private isInternalSubject = new BehaviorSubject<boolean>(false);
  isInternal$: Observable<boolean> = this.isInternalSubject.asObservable();

  // Selected State (used alongside Country for accurate tax lookup)
  private selectedStateSubject = new BehaviorSubject<string>('');
  selectedState$: Observable<string> = this.selectedStateSubject.asObservable();

  // Selected Contact (feeds Invoice Contact / Invoice Contact Address autofill)
  private selectedContactDetailsSubject = new BehaviorSubject<{ name: string; address: string } | null>(null);
  selectedContactDetails$: Observable<{ name: string; address: string } | null> = this.selectedContactDetailsSubject.asObservable();

  // Currency & Tax display state
  private currencyLabelSubject = new BehaviorSubject<string>('');
  currencyLabel$: Observable<string> = this.currencyLabelSubject.asObservable();

  private taxTypeLabelSubject = new BehaviorSubject<string>('VAT Rate: (%)');
  taxTypeLabel$: Observable<string> = this.taxTypeLabelSubject.asObservable();

  private defaultTaxRateSubject = new BehaviorSubject<number>(0);
  defaultTaxRate$: Observable<number> = this.defaultTaxRateSubject.asObservable();

  // Tab Enabling State
  private isAddOtherDetailsDisabledSubject = new BehaviorSubject<boolean>(true);
  isAddOtherDetailsDisabled$: Observable<boolean> = this.isAddOtherDetailsDisabledSubject.asObservable();

  private isViewOtherDetailsDisabledSubject = new BehaviorSubject<boolean>(true);
  isViewOtherDetailsDisabled$: Observable<boolean> = this.isViewOtherDetailsDisabledSubject.asObservable();

  // Active Tab Index
  private selectedIndexSubject = new BehaviorSubject<number>(0);
  selectedIndex$: Observable<number> = this.selectedIndexSubject.asObservable();

  // Loaded Table Data
  private tableDataSubject = new BehaviorSubject<ProjectDetailsTableData>({});
  tableData$: Observable<ProjectDetailsTableData> = this.tableDataSubject.asObservable();

  // User details
  loginName: string = '';
  employeeName: string = '';

  constructor(
    private store: Store,
    private snackBarService: SnackbarService,
    private formLayoutService: FormLayoutService,
    private dialog: MatDialog
  ) {
    this.store.select(getCurrentUser).subscribe(user => {
      if (user) {
        this.loginName = user.LoginName || '';
        this.employeeName = user.Employee || '';
      }
    });
  }

  // Setters & Getters
  setMode(mode: ProjectDetailsMode): void {
    this.modeSubject.next(mode);
  }

  getMode(): ProjectDetailsMode {
    return this.modeSubject.value;
  }

  setSelectedClient(client: string): void {
    this.selectedClientSubject.next(client);
    const code = this.extractCode(client);
    this.selectedClientCodeSubject.next(code);
  }

  getSelectedClient(): string {
    return this.selectedClientSubject.value;
  }

  getSelectedClientCode(): string {
    return this.selectedClientCodeSubject.value;
  }

  setSelectedProject(project: string): void {
    this.selectedProjectSubject.next(project);
    const code = this.extractCode(project);
    this.selectedProjectCodeSubject.next(code);
  }

  getSelectedProject(): string {
    return this.selectedProjectSubject.value;
  }

  getSelectedProjectCode(): string {
    return this.selectedProjectCodeSubject.value;
  }

  setSelectedCountry(countryName: string, countryCode?: string): void {
    this.selectedCountryNameSubject.next(countryName);
    if (countryCode) {
      this.selectedCountryCodeSubject.next(countryCode);
    } else {
      this.selectedCountryCodeSubject.next(countryName);
    }
  }

  getSelectedCountryCode(): string {
    return this.selectedCountryCodeSubject.value;
  }

  getSelectedCountryName(): string {
    return this.selectedCountryNameSubject.value;
  }

  setIsInternal(isInternal: boolean): void {
    this.isInternalSubject.next(isInternal);
  }

  setSelectedState(state: string): void {
    this.selectedStateSubject.next(state);
  }

  getSelectedState(): string {
    return this.selectedStateSubject.value;
  }

  setSelectedContactDetails(contact: { name: string; address: string } | null): void {
    this.selectedContactDetailsSubject.next(contact);
  }

  getSelectedContactDetails(): { name: string; address: string } | null {
    return this.selectedContactDetailsSubject.value;
  }

  setCurrencyLabel(label: string): void {
    this.currencyLabelSubject.next(label);
  }

  setTaxTypeLabel(label: string): void {
    this.taxTypeLabelSubject.next(label);
  }

  setDefaultTaxRate(rate: number): void {
    this.defaultTaxRateSubject.next(rate);
  }

  getDefaultTaxRate(): number {
    return this.defaultTaxRateSubject.value;
  }

  setAddOtherDetailsDisabled(disabled: boolean): void {
    this.isAddOtherDetailsDisabledSubject.next(disabled);
  }

  setViewOtherDetailsDisabled(disabled: boolean): void {
    this.isViewOtherDetailsDisabledSubject.next(disabled);
  }

  setSelectedIndex(index: number): void {
    this.selectedIndexSubject.next(index);
  }

  getSelectedIndex(): number {
    return this.selectedIndexSubject.value;
  }

  setTableData(data: ProjectDetailsTableData): void {
    this.tableDataSubject.next(data);
  }

  getTableData(): ProjectDetailsTableData {
    return this.tableDataSubject.value;
  }

  setUnsavedChanges(hasUnsaved: boolean): void {
    this.formLayoutService.setprojectEntryIsUnsavedChangesPresent(hasUnsaved);
  }

  showNotification(response: any): void {
    if (!response) return;
    if (response['Type'] === 'failure' || response['type'] === 'failure') {
      this.snackBarService.showSnackBar(
        response['Message'] || response['message'] || 'An error occurred',
        'close',
        'failure'
      );
    } else {
      this.snackBarService.showSnackBar(
        response['Message'] || response['message'] || 'Success',
        '',
        'success'
      );
    }
  }

  extractCode(text: string): string {
    if (!text) return '';
    const match = new RegExp('([^-\\s]+)$').exec(text);
    return match ? match[0].trim() : text.trim();
  }

  resetAllState(): void {
    this.setSelectedClient('');
    this.setSelectedProject('');
    this.setSelectedCountry('', '');
    this.setSelectedState('');
    this.setIsInternal(false);
    this.setSelectedContactDetails(null);
    this.setCurrencyLabel('');
    this.setTaxTypeLabel('VAT Rate: (%)');
    this.setDefaultTaxRate(0);
    this.setAddOtherDetailsDisabled(true);
    this.setViewOtherDetailsDisabled(true);
    this.setTableData({});
    this.setUnsavedChanges(false);
  }
}